/**
 * useQuizReviewList — admin/manager Quiz Review & Approval data + mutations
 * (Phase Q3). Reads ALL artifact quizzes (manager-only via RLS) joined to their
 * artifact for title/provider, and exposes the safe mutation paths:
 *
 *   - approve(quizId)   → supabase.rpc('approve_training_quiz', { _quiz_id })
 *   - archive(quizId)   → UPDATE status='archived' (RLS-permitted for managers;
 *                         no dedicated RPC exists and none is needed)
 *   - regenerate(id)    → invoke('generate-training-quiz', { artifactId, regenerate })
 *                         (the ONE AI call; only ever fired on an explicit click)
 *
 * INTEGRITY: this hook never enables trainingQuizMvp, never mutates
 * completion_status/completion_confidence, and reading answers here is allowed
 * only because RLS grants that SELECT to catalog managers (technicians get zero
 * rows). Approvals are gated behind an explicit confirmation in the panel.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { QuizAuthoringQuestion } from '@/lib/training/quiz/quizTypes';
import { computeQuizStale, type QuizStaleState } from '@/lib/training/quiz/quizStale';
import { mapQuizGenError } from '@/lib/training/quiz/quizAdminError';

export type QuizStatus = 'draft' | 'approved' | 'archived';

export interface QuizReviewRow {
  id: string;
  artifactId: string;
  title: string;
  providerName: string | null;
  status: QuizStatus;
  version: number;
  generatedAt: string | null;
  sourceTranscriptHash: string | null;
  /** Current transcript hash — null until Phase 2 wires transcript_hash. */
  transcriptHash: string | null;
  stale: QuizStaleState;
  questionCount: number;
  questions: QuizAuthoringQuestion[];
}

export interface ActionResult {
  ok: boolean;
  error: string | null;
}

/** Supabase returns a to-one embed as an object or a single-element array. */
function firstEmbed<T>(v: unknown): T | null {
  if (Array.isArray(v)) return (v[0] as T) ?? null;
  return (v as T) ?? null;
}

function mapRow(raw: Record<string, unknown>): QuizReviewRow {
  const art = firstEmbed<{ title?: string; provider_name?: string | null }>(
    raw.training_artifacts,
  );
  const questions = Array.isArray(raw.questions) ? (raw.questions as QuizAuthoringQuestion[]) : [];
  const sourceHash = (raw.source_transcript_hash as string | null) ?? null;
  const transcriptHash = null; // Phase 2: populated from training_artifact_transcripts.transcript_hash
  return {
    id: String(raw.id),
    artifactId: String(raw.artifact_id ?? ''),
    title: art?.title ?? '(untitled artifact)',
    providerName: art?.provider_name ?? null,
    status: (raw.status as QuizStatus) ?? 'draft',
    version: Number(raw.version ?? 1),
    generatedAt: (raw.generated_at as string | null) ?? null,
    sourceTranscriptHash: sourceHash,
    transcriptHash,
    stale: computeQuizStale(sourceHash, transcriptHash),
    questionCount: questions.length,
    questions,
  };
}

export function useQuizReviewList() {
  const [rows, setRows] = useState<QuizReviewRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const { data, error: readErr } = await supabase
      .from('training_artifact_quizzes')
      .select(
        'id, artifact_id, status, version, generated_at, source_transcript_hash, questions, training_artifacts!inner(title, provider_name)',
      )
      .order('generated_at', { ascending: false });
    if (readErr) {
      setError('Could not load quizzes. You must be a manager/admin to review quizzes.');
      setRows([]);
      setIsLoading(false);
      return;
    }
    setRows(((data ?? []) as Record<string, unknown>[]).map(mapRow));
    setIsLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Approve a single draft via the audited SECURITY DEFINER RPC. */
  const approve = useCallback(async (quizId: string): Promise<ActionResult> => {
    const { error: rpcErr } = await supabase.rpc('approve_training_quiz', { _quiz_id: quizId });
    if (rpcErr) return { ok: false, error: rpcErr.message || 'Approval failed.' };
    return { ok: true, error: null };
  }, []);

  /** Approve several drafts sequentially (no bulk RPC — one audited call each). */
  const approveMany = useCallback(
    async (quizIds: string[]): Promise<{ approved: string[]; failed: { id: string; error: string }[] }> => {
      const approved: string[] = [];
      const failed: { id: string; error: string }[] = [];
      for (const id of quizIds) {
        const { error: rpcErr } = await supabase.rpc('approve_training_quiz', { _quiz_id: id });
        if (rpcErr) failed.push({ id, error: rpcErr.message || 'Approval failed.' });
        else approved.push(id);
      }
      return { approved, failed };
    },
    [],
  );

  /** Archive/reject a draft — RLS-permitted manager UPDATE (no schema change). */
  const archive = useCallback(async (quizId: string): Promise<ActionResult> => {
    const { error: updErr } = await supabase
      .from('training_artifact_quizzes')
      .update({ status: 'archived' })
      .eq('id', quizId);
    if (updErr) return { ok: false, error: updErr.message || 'Archive failed.' };
    return { ok: true, error: null };
  }, []);

  /** Regenerate a draft — the ONE AI call. Only ever invoked on an explicit click. */
  const regenerate = useCallback(async (artifactId: string): Promise<ActionResult> => {
    const { data, error: fnErr } = await supabase.functions.invoke('generate-training-quiz', {
      body: { artifactId, regenerate: true },
    });
    if (fnErr) {
      const status = (fnErr as { context?: { status?: number } })?.context?.status;
      return { ok: false, error: mapQuizGenError(status, (fnErr as Error).message) };
    }
    const payload = data as { error?: string } | null;
    if (payload?.error) return { ok: false, error: payload.error };
    return { ok: true, error: null };
  }, []);

  return { rows, isLoading, error, refresh, approve, approveMany, archive, regenerate };
}
